import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LEGAL_REVIEW_NOTICE } from "@bw/shared";

interface LegalDocumentProps {
  children: ReactNode;
  title: string;
  /** The version date shown under the title ("Updated Oct 1, 2026"). */
  updated?: string;
}

/**
 * Legal pages: a slim bar (back, title, version date) over one readable
 * column. Section headings are compact; body text is comfortable to read.
 */
export function LegalDocument({ children, title, updated }: LegalDocumentProps) {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default", color: "text.primary" }}>
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            maxWidth: 720,
            mx: "auto",
            px: { xs: 1, sm: 2 },
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton aria-label="Go back" onClick={() => navigate(-1)}>
            <ArrowBackRounded />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: "1.125rem", fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            {updated ? (
              <Typography variant="body2" color="text.secondary">
                Updated {updated}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box component="main" sx={{ maxWidth: 720, mx: "auto", px: 2, pt: 2, pb: 6 }}>
        <Box
          sx={{
            color: "text.primary",
            fontSize: "0.9375rem",
            lineHeight: 1.55,
            "& > :first-of-type": { mt: 0 },
            "& p": { my: 1 },
            "& h2": {
              mt: 3,
              mb: 0.75,
              color: "text.primary",
              fontSize: "1rem",
              fontWeight: 700,
              lineHeight: 1.3,
            },
            "& h3": {
              mt: 2,
              mb: 0.5,
              color: "text.primary",
              fontSize: "0.9375rem",
              fontWeight: 600,
            },
            "& ul": { mt: 0.5, mb: 1.5, pl: 3 },
            "& li": { mb: 0.5 },
            "& a": {
              color: "primary.main",
              textDecorationThickness: "0.08em",
              textUnderlineOffset: "0.16em",
            },
            "& strong": { color: "text.primary" },
          }}
        >
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
            {LEGAL_REVIEW_NOTICE}
          </Alert>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
